/**
 * Call history — Status-style layout: left menu (My Calls / All / Campaigns / Teams), search, type filter, sortable table.
 * Campaign sidebar uses the same synced catalog + visibility as Status (Settings → Campaigns).
 */
import { useState, useEffect, useCallback, useMemo, useRef, type CSSProperties } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Menu, LayoutList, PhoneIncoming, PhoneOutgoing, PhoneOff, Grid3X3, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import { Tooltip } from '../components/Tooltip';
import { DEFAULT_TEAMS, getStatusIcon } from '../lib/statusIcons';
import {
  visibleCampaignTitlesForStatusSidebar,
  type CampaignFilterVisibilityConfig,
} from '../lib/campaignFilterVisibility';
import { parseAgentDirectoryPayload } from '../lib/agentDirectory';

const PAGE_SIZE = 20;
const SIDEBAR_WIDTH_OPEN = 250;
const STORAGE_SIDEBAR_OPEN = 'ipscape-callhistory-sidebar-open';
const STORAGE_MENU_TYPE = 'ipscape-callhistory-menu-type';
const STORAGE_SELECTED_FILTER_CAMPAIGN = 'ipscape-callhistory-selected-filter-campaign';
const STORAGE_SELECTED_FILTER_TEAM = 'ipscape-callhistory-selected-filter-team';
const STORAGE_SORT = 'ipscape-callhistory-sort';

type MenuType = 'campaign' | 'team';
type CallTypeFilter = 'all' | 'inbound' | 'outbound' | 'abandoned';
type SortKey = 'timestamp' | 'agent' | 'call_type' | 'campaign' | 'duration' | 'caller_number';

type CampaignCatalogRow = {
  campaign_id: number;
  campaign_title: string | null;
  campaign_group_name: string | null;
};

interface CallRecord {
  id: string;
  ipscape_activity_id: number;
  ipscape_agent_id?: number;
  user_id?: string;
  agent_name?: string;
  campaign_id?: number;
  campaign_name?: string;
  call_type?: string;
  caller_number?: string;
  called_number?: string;
  duration?: number;
  talk_time?: number;
  wrap_time?: number;
  wrap_code?: string;
  wrap_description?: string;
  timestamp: string;
}

interface AbandonedCallRow {
  id: string;
  activity_id: string;
  campaign_id: number;
  campaign_name?: string;
  time_in_queue?: number;
  callers_number?: string;
  number_called?: string;
  timestamp: string;
}

function readSidebarOpen(): boolean {
  try {
    return localStorage.getItem(STORAGE_SIDEBAR_OPEN) !== '0';
  } catch {
    return true;
  }
}
function readMenuType(): MenuType {
  try {
    const v = localStorage.getItem(STORAGE_MENU_TYPE);
    if (v === 'campaign' || v === 'team') return v;
  } catch {}
  return 'campaign';
}
function readSelectedFilter(menuType: MenuType): string {
  try {
    const key = menuType === 'campaign' ? STORAGE_SELECTED_FILTER_CAMPAIGN : STORAGE_SELECTED_FILTER_TEAM;
    return localStorage.getItem(key) ?? '';
  } catch {}
  return '';
}
function sidebarIconBtn(active: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 36,
    border: 'none',
    borderRadius: 6,
    background: active ? 'rgba(59,130,246,0.2)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--text)',
    cursor: 'pointer',
    marginBottom: '0.25rem',
  };
}

function readSort(): { sort_by: SortKey; sort_order: 'asc' | 'desc' } {
  try {
    const v = localStorage.getItem(STORAGE_SORT);
    if (v) {
      const [sort_by, sort_order] = v.split(':');
      if (['timestamp', 'agent', 'call_type', 'campaign', 'duration', 'caller_number'].includes(sort_by) && (sort_order === 'asc' || sort_order === 'desc'))
        return { sort_by: sort_by as SortKey, sort_order };
    }
  } catch {}
  return { sort_by: 'timestamp', sort_order: 'desc' };
}

export default function CallHistory() {
  const { getToken } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [statusIcons, setStatusIcons] = useState<{ campaigns?: Record<string, string>; teams?: Record<string, string> }>({});
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [abandonedCalls, setAbandonedCalls] = useState<AbandonedCallRow[]>([]);
  const [abandonedTotal, setAbandonedTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedingAbandoned, setSeedingAbandoned] = useState(false);
  const [offset, setOffset] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpen);
  const [menuType, setMenuType] = useState<MenuType>(readMenuType);
  const [selectedFilter, setSelectedFilter] = useState(() => readSelectedFilter(readMenuType()));
  const [catalogRows, setCatalogRows] = useState<CampaignCatalogRow[]>([]);
  const [campaignFilterVisibility, setCampaignFilterVisibility] = useState<CampaignFilterVisibilityConfig | null>(null);
  const [presenceWsUrl, setPresenceWsUrl] = useState('');
  const [teamsFromAgents, setTeamsFromAgents] = useState<string[]>([]);
  const [callTypeFilter, setCallTypeFilter] = useState<CallTypeFilter>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(readSort);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [facetAgents, setFacetAgents] = useState<{ ipscape_agent_id: number; name: string }[]>([]);
  const [retentionDays, setRetentionDays] = useState(1);
  const [dateRangeLabel, setDateRangeLabel] = useState('');

  const showAllAndFacets = userRole === 'admin' || userRole === 'superadmin' || userRole === 'supervisor';

  const workerOrigin = useMemo(() => {
    const ws = presenceWsUrl.trim();
    if (!ws.startsWith('ws')) return '';
    try {
      const normalized = ws.startsWith('wss://')
        ? `https://${ws.slice('wss://'.length)}`
        : `http://${ws.slice('ws://'.length)}`;
      return new URL(normalized).origin;
    } catch {
      return '';
    }
  }, [presenceWsUrl]);

  const catalogTitleToId = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of catalogRows) {
      const t = r.campaign_title?.trim();
      if (t) m.set(t, r.campaign_id);
    }
    return m;
  }, [catalogRows]);

  /** Same campaign list as Status (synced catalog + Settings → Campaigns visibility). */
  const sidebarCampaigns = useMemo(
    () => visibleCampaignTitlesForStatusSidebar(catalogRows, campaignFilterVisibility),
    [catalogRows, campaignFilterVisibility]
  );

  const sidebarTeams = useMemo(() => {
    if (teamsFromAgents.length > 0) return teamsFromAgents;
    return DEFAULT_TEAMS;
  }, [teamsFromAgents]);

  const fetchCalls = useCallback(
    async (off: number) => {
      const token = await getToken();
      if (!token) return;
      setLoading(true);
      setError(null);
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(off));
      params.set('sort_by', sort.sort_by);
      params.set('sort_order', sort.sort_order);
      if (callTypeFilter !== 'all') params.set('call_type', callTypeFilter);
      if (search.trim()) params.set('q', search.trim());
      if (selectedFilter === 'my') params.set('my_calls', '1');
      else if (selectedFilter !== 'all' && selectedFilter) {
        if (menuType === 'campaign') params.set('campaign', selectedFilter);
        else params.set('team', selectedFilter);
      }
      if (selectedAgentId) params.set('ipscape_agent_id', selectedAgentId);
      try {
        const res = await fetch(`${base}/api/calls?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setCalls(data.items ?? []);
        setTotal(data.total ?? 0);
        if (typeof data.retention_days === 'number') setRetentionDays(data.retention_days);
        if (data.date_from && data.date_to) {
          setDateRangeLabel(`${data.date_from} → ${data.date_to}`);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setCalls([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [getToken, sort.sort_by, sort.sort_order, callTypeFilter, search, selectedFilter, menuType, selectedAgentId]
  );

  const loadFacets = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const params = new URLSearchParams({ limit: '1', offset: '0', facets: '1' });
      const res = await fetch(`${base}/api/calls?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data.agents)) setFacetAgents(data.agents);
      if (typeof data.retention_days === 'number') setRetentionDays(data.retention_days);
    } catch {
      /* optional */
    }
  }, [getToken]);

  const fetchAbandonedCalls = useCallback(
    async (off: number) => {
      const token = await getToken();
      if (!token) return;
      setLoading(true);
      setError(null);
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(off));
      if (menuType === 'campaign' && selectedFilter && selectedFilter !== 'all' && selectedFilter !== 'my') {
        const campaignId = catalogTitleToId.get(selectedFilter);
        if (campaignId != null) params.set('campaign_id', String(campaignId));
        else params.set('campaign', selectedFilter);
      }
      try {
        const res = await fetch(`${base}/api/abandoned-calls?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setAbandonedCalls(data.items ?? []);
        setAbandonedTotal(data.total ?? 0);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setAbandonedCalls([]);
        setAbandonedTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [getToken, selectedFilter, menuType, catalogTitleToId]
  );

  useEffect(() => {
    if (callTypeFilter === 'abandoned') {
      fetchAbandonedCalls(offset);
    } else {
      fetchCalls(offset);
    }
  }, [offset, callTypeFilter, fetchCalls, fetchAbandonedCalls]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      try {
        const res = await fetch(`${base}/api/users/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data.user) setUserRole(data.user.role ?? null);
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (userRole) void loadFacets();
  }, [userRole, loadFacets]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      try {
        const res = await fetch(
          `${base}/api/config?keys=status_icons,presence_ws_url,campaign_filter_visibility`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data.status_icons && typeof data.status_icons === 'object') {
            setStatusIcons(data.status_icons as { campaigns?: Record<string, string>; teams?: Record<string, string> });
          }
          if (!cancelled && typeof data.presence_ws_url === 'string' && data.presence_ws_url.trim()) {
            setPresenceWsUrl(data.presence_ws_url.trim());
          }
          const vis = data.campaign_filter_visibility;
          if (!cancelled) {
            if (vis && typeof vis === 'object' && !Array.isArray(vis)) {
              setCampaignFilterVisibility(vis as CampaignFilterVisibilityConfig);
            } else {
              setCampaignFilterVisibility(null);
            }
          }
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!workerOrigin) return;
      try {
        const res = await fetch(`${workerOrigin}/campaigns/catalog`);
        if (res.ok) {
          const data = (await res.json()) as { campaigns?: CampaignCatalogRow[] };
          if (Array.isArray(data.campaigns) && !cancelled) setCatalogRows(data.campaigns);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workerOrigin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!workerOrigin) return;
      try {
        const res = await fetch(`${workerOrigin}/agents/catalog`);
        if (!res.ok) return;
        const data = (await res.json()) as { agents?: { agent_id: number; payload: string }[] };
        const teamSet = new Set<string>();
        for (const r of data.agents ?? []) {
          const row = parseAgentDirectoryPayload(r.agent_id, r.payload);
          const team = row?.team?.trim();
          if (team) teamSet.add(team);
        }
        if (!cancelled) {
          setTeamsFromAgents(Array.from(teamSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workerOrigin]);

  useEffect(() => {
    if (menuType !== 'campaign' || !selectedFilter) return;
    if (selectedFilter === 'my' || selectedFilter === 'all') return;
    if (!sidebarCampaigns.includes(selectedFilter)) setSelectedFilter('');
  }, [menuType, sidebarCampaigns, selectedFilter]);

  /** Once catalog campaigns load, default to Campaigns sidebar (same as Status; fixes stale team menu in localStorage). */
  const didPreferCampaignSidebar = useRef(false);
  useEffect(() => {
    if (sidebarCampaigns.length === 0 || didPreferCampaignSidebar.current) return;
    didPreferCampaignSidebar.current = true;
    if (menuType !== 'campaign') {
      setMenuType('campaign');
      if (selectedFilter !== 'my') {
        const saved = readSelectedFilter('campaign');
        setSelectedFilter(saved || (selectedFilter === 'all' ? 'all' : ''));
      }
    }
  }, [sidebarCampaigns.length, menuType, selectedFilter]);

  const seedSampleCalls = async () => {
    const token = await getToken();
    if (!token) return;
    setSeeding(true);
    setError(null);
    setSuccessMessage(null);
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/seed-calls`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSuccessMessage(`Seeded ${data.inserted ?? 500} calls.`);
      setOffset(0);
      await fetchCalls(0);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const seedAbandonedCalls = async () => {
    const token = await getToken();
    if (!token) return;
    setSeedingAbandoned(true);
    setError(null);
    setSuccessMessage(null);
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/seed-abandoned-calls`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSuccessMessage(`Seeded ${data.inserted ?? 0} abandoned calls.`);
      setTimeout(() => setSuccessMessage(null), 5000);
      if (callTypeFilter === 'abandoned') {
        setOffset(0);
        fetchAbandonedCalls(0);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Seed abandoned failed');
    } finally {
      setSeedingAbandoned(false);
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_SIDEBAR_OPEN, sidebarOpen ? '1' : '0');
    } catch {}
  }, [sidebarOpen]);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_MENU_TYPE, menuType);
    } catch {}
  }, [menuType]);
  useEffect(() => {
    try {
      const key = menuType === 'campaign' ? STORAGE_SELECTED_FILTER_CAMPAIGN : STORAGE_SELECTED_FILTER_TEAM;
      localStorage.setItem(key, selectedFilter);
    } catch {}
  }, [menuType, selectedFilter]);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_SORT, `${sort.sort_by}:${sort.sort_order}`);
    } catch {}
  }, [sort]);

  const menuItems = menuType === 'campaign' ? sidebarCampaigns : sidebarTeams;
  const iconMap = menuType === 'campaign' ? (statusIcons.campaigns || {}) : (statusIcons.teams || {});

  const handleSort = (key: SortKey) => {
    setSort((prev) => ({
      sort_by: key,
      sort_order: prev.sort_by === key && prev.sort_order === 'desc' ? 'asc' : 'desc',
    }));
    setOffset(0);
  };

  const formatDuration = (sec?: number) => {
    if (sec == null) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };
  const formatTimestamp = (ts: string) => {
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  const displayTotal = callTypeFilter === 'abandoned' ? abandonedTotal : total;
  const totalPages = Math.ceil(displayTotal / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const formatTimeInQueue = (sec?: number) => {
    if (sec == null) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const filteredAbandoned = search.trim()
    ? abandonedCalls.filter(
        (a) =>
          (a.callers_number ?? '').toLowerCase().includes(search.toLowerCase()) ||
          (a.number_called ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : abandonedCalls;

  return (
    <div style={{ display: 'flex', width: '100%', minHeight: 'calc(100vh - 52px)', gap: 0 }}>
      {/* Left sidebar */}
      <aside
        style={{
          width: sidebarOpen ? SIDEBAR_WIDTH_OPEN : 52,
          minWidth: sidebarOpen ? SIDEBAR_WIDTH_OPEN : 52,
          maxWidth: sidebarOpen ? SIDEBAR_WIDTH_OPEN : 52,
          flexShrink: 0,
          background: 'var(--widget-bg)',
          borderRight: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease',
        }}
      >
        <div style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Tooltip content={sidebarOpen ? 'Collapse menu' : 'Expand menu'}>
            <button
              type="button"
              onClick={() => setSidebarOpen((o) => !o)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'transparent',
                color: 'var(--text)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label={sidebarOpen ? 'Collapse menu' : 'Expand menu'}
            >
              <Menu size={20} strokeWidth={2} />
            </button>
          </Tooltip>
          {sidebarOpen && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              {menuType === 'campaign' ? 'Campaigns' : 'Teams'}
            </span>
          )}
        </div>
        <nav
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '0.5rem',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {sidebarOpen ? (
            <>
              <Tooltip content="My calls only">
                <button
                  type="button"
                  onClick={() => { setSelectedFilter('my'); setOffset(0); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    textAlign: 'left',
                    border: 'none',
                    borderRadius: 6,
                    background: selectedFilter === 'my' ? 'rgba(59,130,246,0.2)' : 'transparent',
                    color: selectedFilter === 'my' ? 'var(--accent)' : 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: selectedFilter === 'my' ? 600 : 400,
                    marginBottom: '0.35rem',
                  }}
                >
                  <LayoutList size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
                  My Calls
                </button>
              </Tooltip>
              {showAllAndFacets && (
                <button
                  type="button"
                  onClick={() => { setSelectedFilter('all'); setOffset(0); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    textAlign: 'left',
                    border: 'none',
                    borderRadius: 6,
                    background: selectedFilter === 'all' ? 'rgba(59,130,246,0.2)' : 'transparent',
                    color: selectedFilter === 'all' ? 'var(--accent)' : 'var(--text)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: selectedFilter === 'all' ? 600 : 400,
                    marginBottom: '0.35rem',
                  }}
                >
                  <LayoutList size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
                  All
                </button>
              )}
              <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem', width: '100%' }}>
                <button
                  type="button"
                  onClick={() => {
                    setMenuType('campaign');
                    setSelectedFilter(readSelectedFilter('campaign') || 'all');
                    setOffset(0);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.35rem 0.5rem',
                    fontSize: '0.75rem',
                    border: 'none',
                    borderRadius: 4,
                    background: menuType === 'campaign' ? 'var(--accent)' : 'rgba(255,255,255,0.08)',
                    color: menuType === 'campaign' ? '#fff' : 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  Campaigns
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuType('team');
                    setSelectedFilter(readSelectedFilter('team'));
                    setOffset(0);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.35rem 0.5rem',
                    fontSize: '0.75rem',
                    border: 'none',
                    borderRadius: 4,
                    background: menuType === 'team' ? 'var(--accent)' : 'rgba(255,255,255,0.08)',
                    color: menuType === 'team' ? '#fff' : 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  Teams
                </button>
              </div>
              {menuItems.length > 0 ? (
                menuItems.map((item) => {
                  const IconComponent = getStatusIcon(iconMap[item]);
                  return (
                    <Tooltip key={item} content={item} placement="top">
                      <button
                        type="button"
                        onClick={() => { setSelectedFilter(item); setOffset(0); }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          textAlign: 'left',
                          border: 'none',
                          borderRadius: 6,
                          background: selectedFilter === item ? 'rgba(59,130,246,0.2)' : 'transparent',
                          color: selectedFilter === item ? 'var(--accent)' : 'var(--text)',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          fontWeight: selectedFilter === item ? 600 : 400,
                          marginBottom: '0.25rem',
                        }}
                      >
                        <IconComponent size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item}
                        </span>
                      </button>
                    </Tooltip>
                  );
                })
              ) : (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '0.25rem 0.5rem', margin: 0 }}>
                  {menuType === 'campaign'
                    ? workerOrigin
                      ? 'No campaigns visible — check Settings → Campaigns'
                      : 'Set presence URL in Settings → Integrations'
                    : 'No teams in agent directory yet'}
                </p>
              )}
            </>
          ) : (
            <>
              <Tooltip content="My calls">
                <button
                  type="button"
                  onClick={() => { setSelectedFilter('my'); setOffset(0); }}
                  style={sidebarIconBtn(selectedFilter === 'my')}
                  aria-label="My calls"
                >
                  <LayoutList size={18} />
                </button>
              </Tooltip>
              {showAllAndFacets && (
                <Tooltip content="All">
                  <button
                    type="button"
                    onClick={() => { setSelectedFilter('all'); setOffset(0); }}
                    style={sidebarIconBtn(selectedFilter === 'all')}
                    aria-label="All calls"
                  >
                    <LayoutList size={18} />
                  </button>
                </Tooltip>
              )}
              {menuItems.slice(0, 12).map((item) => {
                const IconComponent = getStatusIcon(iconMap[item]);
                return (
                  <Tooltip key={item} content={item}>
                    <button
                      type="button"
                      onClick={() => { setSelectedFilter(item); setOffset(0); }}
                      style={sidebarIconBtn(selectedFilter === item)}
                      aria-label={item}
                    >
                      <IconComponent size={18} />
                    </button>
                  </Tooltip>
                );
              })}
            </>
          )}
        </nav>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '1rem', overflow: 'auto' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
          <input
            type="search"
            placeholder={callTypeFilter === 'abandoned' ? 'Search by caller number' : 'Search by agent or CLI'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setOffset(0);
                if (callTypeFilter === 'abandoned') void fetchAbandonedCalls(0);
                else void fetchCalls(0);
              }
            }}
            style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', minWidth: '180px' }}
          />
          {showAllAndFacets && callTypeFilter !== 'abandoned' && (
            <select
              value={selectedAgentId}
              onChange={(e) => {
                setSelectedAgentId(e.target.value);
                setOffset(0);
              }}
              aria-label="Filter by agent"
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: 6,
                border: '1px solid var(--widget-bg)',
                background: 'var(--widget-bg)',
                color: 'var(--text)',
                minWidth: 160,
                maxWidth: 220,
              }}
            >
              <option value="">All agents</option>
              {facetAgents.map((a) => (
                <option key={a.ipscape_agent_id} value={String(a.ipscape_agent_id)}>
                  {a.name}
                </option>
              ))}
            </select>
          )}
          {callTypeFilter !== 'abandoned' && dateRangeLabel && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }} title={`Retention: ${retentionDays} day(s)`}>
              {dateRangeLabel}
            </span>
          )}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0,
              padding: 2,
              borderRadius: 8,
              border: '1px solid var(--widget-bg)',
              background: 'var(--widget-bg)',
            }}
          >
            {(
              [
                { value: 'all' as const, label: 'All', Icon: Grid3X3 },
                { value: 'inbound' as const, label: 'Inbound', Icon: PhoneIncoming },
                { value: 'outbound' as const, label: 'Outbound', Icon: PhoneOutgoing },
                { value: 'abandoned' as const, label: 'Abandoned calls', Icon: PhoneOff },
              ] as const
            ).map(({ value, label, Icon }) => {
              const selected = callTypeFilter === value;
              return (
                <Tooltip key={value} content={label} placement="bottom">
                  <button
                    type="button"
                    onClick={() => {
                      setCallTypeFilter(value);
                      setOffset(0);
                    }}
                    aria-label={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      border: 'none',
                      borderRadius: 6,
                      background: selected ? 'var(--bg)' : 'transparent',
                      color: selected ? 'var(--accent)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      boxShadow: selected ? '0 1px 2px rgba(0,0,0,0.2)' : undefined,
                    }}
                  >
                    <Icon size={20} strokeWidth={2} />
                  </button>
                </Tooltip>
              );
            })}
          </div>
          {(userRole === 'superadmin' || userRole === 'admin') && (
            <>
              <button
                type="button"
                onClick={seedSampleCalls}
                disabled={seeding}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.8125rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6,
                  color: 'var(--text-muted)',
                  cursor: seeding ? 'not-allowed' : 'pointer',
                }}
              >
                {seeding ? 'Seeding…' : 'Seed 500'}
              </button>
              <button
                type="button"
                onClick={seedAbandonedCalls}
                disabled={seedingAbandoned}
                style={{
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.8125rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6,
                  color: 'var(--text-muted)',
                  cursor: seedingAbandoned ? 'not-allowed' : 'pointer',
                }}
              >
                {seedingAbandoned ? 'Seeding…' : 'Seed abandoned'}
              </button>
            </>
          )}
        </div>

        {error && <p style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '0.875rem' }}>{error}</p>}
        {successMessage && <p style={{ color: 'var(--accent)', marginBottom: '1rem', fontSize: '0.875rem' }}>{successMessage}</p>}

        {loading ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</p>
        ) : callTypeFilter === 'abandoned' ? (
          abandonedCalls.length === 0 && !search.trim() ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No abandoned calls found.</p>
          ) : (
            <>
              <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Date / Time</th>
                      <th style={{ padding: '0.5rem 0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Campaign</th>
                      <th style={{ padding: '0.5rem 0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Caller</th>
                      <th style={{ padding: '0.5rem 0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Time in queue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(search.trim() ? filteredAbandoned : abandonedCalls).map((a) => (
                      <tr key={a.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <td style={{ padding: '0.5rem 0.75rem' }}>{formatTimestamp(a.timestamp)}</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>{a.campaign_name ?? a.campaign_id}</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>{a.callers_number || a.number_called || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>{formatTimeInQueue(a.time_in_queue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  {displayTotal} abandoned call{displayTotal !== 1 ? 's' : ''}
                  {search.trim() && filteredAbandoned.length !== abandonedCalls.length && ` (${filteredAbandoned.length} matching search)`}
                </span>
                {totalPages > 1 && (
                  <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <button
                      type="button"
                      disabled={offset === 0}
                      onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                      style={{
                        padding: '0.25rem 0.5rem',
                        background: 'var(--widget-bg)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 4,
                        color: 'var(--text)',
                        cursor: offset === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: '0.875rem' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={offset + PAGE_SIZE >= displayTotal}
                      onClick={() => setOffset((o) => o + PAGE_SIZE)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        background: 'var(--widget-bg)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 4,
                        color: 'var(--text)',
                        cursor: offset + PAGE_SIZE >= displayTotal ? 'not-allowed' : 'pointer',
                        fontSize: '0.875rem',
                      }}
                    >
                      Next
                    </button>
                  </span>
                )}
              </div>
            </>
          )
        ) : calls.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No calls found.</p>
        ) : (
          <>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', textAlign: 'left' }}>
                    {[
                      { key: 'timestamp' as const, label: 'Time' },
                      { key: 'agent' as const, label: 'Agent' },
                      { key: 'call_type' as const, label: 'Type' },
                      { key: 'campaign' as const, label: 'Campaign' },
                      { key: 'caller_number' as const, label: 'Caller / Called' },
                      { key: 'duration' as const, label: 'Duration' },
                      { key: null, label: 'Wrap' },
                    ].map(({ key, label }) => {
                      const isSortable = key !== null;
                      const isActive = isSortable && sort.sort_by === key;
                      const title = isSortable
                        ? isActive
                          ? `Sorted ${sort.sort_order === 'asc' ? 'ascending' : 'descending'} (click to toggle)`
                          : 'Click to sort'
                        : undefined;
                      return (
                        <th
                          key={key ?? label}
                          title={title}
                          style={{
                            padding: '0.5rem 0.75rem',
                            color: 'var(--text-muted)',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            cursor: isSortable ? 'pointer' : undefined,
                            userSelect: 'none',
                          }}
                          onClick={() => key && handleSort(key)}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                            {label}
                            {isSortable && (
                              <span style={{ color: isActive ? 'var(--accent)' : 'var(--text-muted)' }} aria-hidden>
                                {isActive ? (
                                  sort.sort_order === 'asc' ? <ChevronUp size={16} strokeWidth={2} /> : <ChevronDown size={16} strokeWidth={2} />
                                ) : (
                                  <ArrowUpDown size={14} strokeWidth={2} />
                                )}
                              </span>
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c) => (
                    <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{formatTimestamp(c.timestamp)}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{c.agent_name || '—'}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{c.call_type || '—'}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{c.campaign_name || '—'}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        {c.call_type === 'inbound' ? (c.caller_number || '—') : (c.called_number || '—')}
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{formatDuration(c.duration)}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{c.wrap_description || c.wrap_code || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                {total} call{total !== 1 ? 's' : ''}
              </span>
              {totalPages > 1 && (
                <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button
                    type="button"
                    disabled={offset === 0}
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                    style={{
                      padding: '0.25rem 0.5rem',
                      background: 'var(--widget-bg)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 4,
                      color: 'var(--text)',
                      cursor: offset === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '0.875rem' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                    style={{
                      padding: '0.25rem 0.5rem',
                      background: 'var(--widget-bg)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 4,
                      color: 'var(--text)',
                      cursor: offset + PAGE_SIZE >= total ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                    }}
                  >
                    Next
                  </button>
                </span>
              )}
            </div>
          </>
        )}
      </div>

    </div>
  );
}
